// src/app/modules/catalogues/CatalogueManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, X, Trash2, Edit, BookOpen, Search, Check, Eye,
    Image as ImageIcon, FileText, Info
} from "lucide-react";
import {
    fetchCataloguesRequest,
    createCatalogueRequest,
    updateCatalogueRequest,
    deleteCatalogueRequest,
    resetCatalogueState,
} from "../../../../redux/actions/cataloguesActions";
import { fetchFormationsRequest } from "../../../../redux/actions/formationsActions";

import "../RessourcesStyles/CatalogueManagement.css";
import { API_BASE_URL } from "../../../../../api";

const emptyForm = {
    nom: "",
    reference: "",
    description: "",
    status: 1,
    formationIds: [],
    imageFile: null,
    file: null,
};

export default function CatalogueManagement() {
    const dispatch = useDispatch();

    const { catalogues, loading, success } = useSelector((state) => state.catalogues || {});
    const formations = useSelector((state) => state.formations.list || []);

    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const [form, setForm] = useState(emptyForm);
    const [tableQ, setTableQ] = useState("");
    const [searchFormation, setSearchFormation] = useState("");
    const [selectedCat, setSelectedCat] = useState(null);
    const [detailFormationSearch, setDetailFormationSearch] = useState("");
    const [detailFormationPage, setDetailFormationPage] = useState(1);

    // New states for pagination, cascading filters, and multi-selection
    const [selectedDomaine, setSelectedDomaine] = useState("");
    const [selectedTheme, setSelectedTheme] = useState("");
    const [selectedFormationIds, setSelectedFormationIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 5;

    const cataloguesList = useMemo(() => (Array.isArray(catalogues) ? catalogues : []), [catalogues]);
    const formationsList = useMemo(() => (Array.isArray(formations) ? formations : []), [formations]);

    const getImageUrl = (filename) => filename ? `${API_BASE_URL}/api/catalogues/images/${filename}` : null;
    const getFileUrl  = (filename) => filename ? `${API_BASE_URL}/api/catalogues/files/${filename}` : null;

    useEffect(() => {
        dispatch(fetchCataloguesRequest());
        dispatch(fetchFormationsRequest());
    }, [dispatch]);

    useEffect(() => {
        if (success) closeModal();
    }, [success]);

    const filteredCatalogues = useMemo(() => {
        const q = tableQ.trim().toLowerCase();
        if (!q) return cataloguesList;
        return cataloguesList.filter(c =>
            `${c.nom || ""} ${c.reference || ""}`.toLowerCase().includes(q)
        );
    }, [cataloguesList, tableQ]);

    // Build the list of domains dynamically from formations List
    const domaines = useMemo(() => {
        const list = formationsList.map(f => {
            if (f?.domaine) return f.domaine;
            if (f?.domaineId || f?.domaineNom) {
                return {
                    id: f.domaineId || f.domaineNom,
                    reference: f.domaineReference || f.domaineId || f.domaineNom,
                    nom: f.domaineNom || f.domaineId,
                    libelle: f.domaineNom,
                    name: f.domaineNom
                };
            }
            return null;
        }).filter(Boolean);

        return Array.from(
            new Map(
                list.map(d => [d.id || d.reference || d.nom || d.libelle || d.name, d])
            ).values()
        );
    }, [formationsList]);

    // Build the list of themes dynamically, filtered by selected domain
    const themes = useMemo(() => {
        const list = formationsList
            .filter(f => {
                if (!selectedDomaine) return true;
                const domId = f?.domaine?.id || f?.domaine?.reference || f?.domaineId || f?.domaineReference || f?.domaineNom;
                return domId?.toString() === selectedDomaine.toString();
            })
            .map(f => {
                if (f?.theme) return f.theme;
                if (f?.themeId || f?.themeNom) {
                    return {
                        id: f.themeId || f.themeNom,
                        reference: f.themeReference || f.themeId || f.themeNom,
                        nom: f.themeNom || f.themeId,
                        libelle: f.themeNom,
                        name: f.themeNom
                    };
                }
                return null;
            })
            .filter(Boolean);

        return Array.from(
            new Map(
                list.map(t => [t.id || t.reference || t.nom || t.libelle || t.name, t])
            ).values()
        );
    }, [formationsList, selectedDomaine]);

    // Filter formations dynamically based on text search, domain filter and theme filter
    const filteredFormations = useMemo(() => {
        const q = (searchFormation || "").toLowerCase().trim();
        let list = formationsList;

        if (q) {
            list = list.filter((f) =>
                `${f.reference || ""} ${f.description || ""}`.toLowerCase().includes(q)
            );
        }

        if (selectedDomaine) {
            list = list.filter((f) => {
                const domId = f?.domaine?.id || f?.domaine?.reference || f?.domaineId || f?.domaineReference || f?.domaineNom;
                return domId?.toString() === selectedDomaine.toString();
            });
        }

        if (selectedTheme) {
            list = list.filter((f) => {
                const thId = f?.theme?.id || f?.theme?.reference || f?.themeId || f?.themeReference || f?.themeNom;
                return thId?.toString() === selectedTheme.toString();
            });
        }

        return list;
    }, [formationsList, searchFormation, selectedDomaine, selectedTheme]);

    // Pagination configuration
    const totalPages = useMemo(() => {
        return Math.ceil(filteredFormations.length / itemsPerPage) || 1;
    }, [filteredFormations, itemsPerPage]);

    const paginatedFormations = useMemo(() => {
        return filteredFormations.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filteredFormations, currentPage, itemsPerPage]);

    // Multi-selection operations
    const toggleFormationSelection = (formationId) => {
        setSelectedFormationIds(prev =>
            prev.includes(formationId)
                ? prev.filter(id => id !== formationId)
                : [...prev, formationId]
        );
    };

    const filteredFormationIds = useMemo(() => {
        return filteredFormations.map(f => f.id).filter(Boolean);
    }, [filteredFormations]);

    const areAllFilteredSelected = useMemo(() => {
        return filteredFormationIds.length > 0 &&
            filteredFormationIds.every(id => selectedFormationIds.includes(id));
    }, [filteredFormationIds, selectedFormationIds]);

    const toggleSelectAllFiltered = () => {
        if (areAllFilteredSelected) {
            setSelectedFormationIds(prev =>
                prev.filter(id => !filteredFormationIds.includes(id))
            );
        } else {
            setSelectedFormationIds(prev =>
                Array.from(new Set([...prev, ...filteredFormationIds]))
            );
        }
    };

    // Safe empty page hook
    useEffect(() => {
        const total = Math.ceil(filteredFormations.length / itemsPerPage) || 1;
        if (currentPage > total) {
            setCurrentPage(total);
        }
    }, [filteredFormations.length, currentPage]);

    const associatedFormations = useMemo(() => {
        if (!selectedCat) return [];
        const ids = Array.isArray(selectedCat?.formationIds) ? selectedCat.formationIds : [];
        return formationsList.filter((f) => ids.includes(f.id));
    }, [selectedCat, formationsList]);

    const filteredAssociatedFormations = useMemo(() => {
        const term = (detailFormationSearch || "").toLowerCase().trim();
        if (!term) return associatedFormations;

        return associatedFormations.filter((formation) => {
            const title = formation.title || formation.nom || formation.name || formation.description || "";
            const code = formation.code || formation.reference || "";
            const domaine = formation.domaine?.name || formation.domaine?.nom || formation.domaineNom || "";
            const theme = formation.theme?.name || formation.theme?.nom || formation.themeNom || "";

            return `${title} ${code} ${domaine} ${theme}`.toLowerCase().includes(term);
        });
    }, [associatedFormations, detailFormationSearch]);

    const detailPageSize = 10;
    const detailTotalPages = Math.max(1, Math.ceil(filteredAssociatedFormations.length / detailPageSize));
    const detailCurrentPage = Math.min(Math.max(detailFormationPage, 1), detailTotalPages);
    const paginatedAssociatedFormations = useMemo(() => {
        const start = (detailCurrentPage - 1) * detailPageSize;
        return filteredAssociatedFormations.slice(start, start + detailPageSize);
    }, [filteredAssociatedFormations, detailCurrentPage]);

    useEffect(() => {
        setDetailFormationPage(1);
    }, [detailFormationSearch]);

    useEffect(() => {
        setDetailFormationSearch("");
        setDetailFormationPage(1);
    }, [selectedCat]);

    const openAdd = () => {
        dispatch(resetCatalogueState());
        setIsEdit(false);
        setCurrentId(null);
        setSelectedCat(null);
        setForm(emptyForm);
        setSelectedDomaine("");
        setSelectedTheme("");
        setSelectedFormationIds([]);
        setCurrentPage(1);
        setOpen(true);
    };

    const handleOpenDetails = (c) => {
        setSelectedCat(c);
        setDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedCat(null);
        setDetailFormationSearch("");
        setDetailFormationPage(1);
    };

    const openEdit = (c) => {
        dispatch(resetCatalogueState());
        setIsEdit(true);
        setCurrentId(c?.id ?? null);
        setForm({
            nom: c?.nom || "",
            reference: c?.reference || "",
            description: c?.description || "",
            status: c?.status ?? 1,
            formationIds: Array.isArray(c?.formationIds) ? c.formationIds : [],
            imageFile: null,
            file: null,
            picture: c?.picture || null,
            attachment: c?.attachment || null,
        });
        setSelectedDomaine("");
        setSelectedTheme("");
        setSelectedFormationIds(Array.isArray(c?.formationIds) ? c.formationIds : []);
        setCurrentPage(1);
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setIsEdit(false);
        setCurrentId(null);
        setSelectedCat(null);
        setForm(emptyForm);
        setSearchFormation("");
        setSelectedDomaine("");
        setSelectedTheme("");
        setSelectedFormationIds([]);
        setCurrentPage(1);
    };

    const onSubmit = () => {
        if (!form.reference?.trim()) {
            Swal.fire({ icon: "warning", text: "Référence obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }
        if (!form.nom?.trim()) {
            Swal.fire({ icon: "warning", text: "Nom obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nom: form.nom,
            reference: form.reference,
            description: form.description,
            status: form.status,
            formationIds: selectedFormationIds || [],
            imageFile: form.imageFile,
            file: form.file,
        };

        if (isEdit && currentId !== null && currentId !== undefined) {
            dispatch(updateCatalogueRequest(currentId, payload));
        } else {
            dispatch(createCatalogueRequest(payload));
        }
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irréversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#2f4595",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteCatalogueRequest(id));
        });
    };

    /* ─────────────────────────────────────────────
       RENDU
    ───────────────────────────────────────────── */
    return (
        <div className="cat-page">

            {/* ── Header (style ParamNF) ── */}
            <div className="cat-header">
                <div>
                    <h2>Gestion des catalogues</h2>
                </div>
                <div className="cat-header-actions">
                    <button className="cat-btn cat-btn-primary catalogue-add-btn" onClick={openAdd} type="button">
                        <Plus size={16} /> Ajouter
                    </button>
                </div>
            </div>

            {/* ── Card principale avec body (style ParamNF) ── */}
            <div className="cat-card">
                <div className="cat-body">

                    {/* Section top : recherche à gauche, boutons à droite */}
                    <div className="cat-section-top">
                        <div className="cat-search-wrap">
                            <Search size={14} className="cat-search-icon" />
                            <input
                                type="text"
                                className="cat-search"
                                value={tableQ}
                                onChange={(e) => setTableQ(e.target.value)}
                                placeholder="Rechercher par nom ou référence..."
                            />
                        </div>

                    </div>

                    {/* Tableau (style ParamNF nfTableWrap / nfTable) */}
                    <div className="cat-table-wrap">
                        <table className="cat-table">
                            <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Référence</th>
                                <th>Statut</th>
                                <th>Formations</th>
                                <th className="is-right" style={{ width: 120 }}>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="cat-loading">Chargement...</td>
                                </tr>
                            ) : filteredCatalogues.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="cat-empty-cell">
                                        Aucun catalogue trouvé
                                    </td>
                                </tr>
                            ) : (
                                filteredCatalogues.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <BookOpen size={14} style={{ color: "#dc3545", flexShrink: 0 }} />
                                                <span style={{ fontWeight: 700, color: "#2f4595" }}>{c.nom}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="cat-badge cat-badge--blue">{c.reference}</span>
                                        </td>
                                        <td>
                                                <span className={`cat-pill ${c.status === 1 ? "ok" : "off"}`}>
                                                    {c.status === 1 ? "Actif" : "Inactif"}
                                                </span>
                                        </td>
                                        <td>
                                                <span className="cat-badge cat-badge--grey">
                                                    {(c.formationIds || []).length} formation(s)
                                                </span>
                                        </td>
                                        <td className="is-right">
                                            <div className="cat-actions">
                                                <button className="cat-icon-btn" title="Détails" onClick={() => handleOpenDetails(c)} type="button">
                                                    <Eye size={16} />
                                                </button>
                                                <button className="cat-icon-btn" title="Modifier" onClick={() => openEdit(c)} type="button">
                                                    <Edit size={16} />
                                                </button>
                                                <button className="cat-icon-btn cat-icon-btn--danger" title="Supprimer" onClick={() => onDelete(c.id)} type="button">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Compteur */}
                    {/*<div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: "#64748b" }}>*/}
                    {/*    {loading ? "…" : `${filteredCatalogues.length} catalogue(s)`}*/}
                    {/*</div>*/}
                </div>
            </div>

            {/* ── Modal CRUD ── */}
            {open && (
                <div className="cat-modal-overlay" onClick={closeModal}>
                    <div className="cat-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cat-modal-header">
                            <h3>
                                <BookOpen size={16} style={{ color: "#dc3545" }} />
                                {isEdit ? "Modifier le catalogue" : "Ajouter un catalogue"}
                            </h3>
                            <button className="cat-x" onClick={closeModal} type="button"><X size={18} /></button>
                        </div>

                        <div className="cat-modal-body">
                            <div className="cat-grid">
                                <div className="cat-field">
                                    <label>Nom *</label>
                                    <input
                                        value={form.nom}
                                        onChange={(e) => setForm({ ...form, nom: e.target.value })}
                                        placeholder="Nom catalogue"
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Référence *</label>
                                    <input
                                        value={form.reference}
                                        onChange={(e) => setForm({ ...form, reference: e.target.value })}
                                        placeholder="CAT-2026"
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Statut</label>
                                    <select value={form.status} onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}>
                                        <option value={1}>Actif</option>
                                        <option value={0}>Inactif</option>
                                    </select>
                                </div>

                                <div className="cat-field cat-span-2">
                                    <label>Description</label>
                                    <textarea
                                        rows={3}
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                        placeholder="Description..."
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Image (optionnel)</label>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {form.picture && !form.imageFile && (
                                            <div className="cat-preview">
                                                <img src={getImageUrl(form.picture)} alt="Catalogue" />
                                                <ImageIcon size={14} style={{ color: "#6b7280" }} />
                                            </div>
                                        )}
                                        {form.imageFile && (
                                            <div className="cat-preview">
                                                <span className="cat-file-pill">Nouvelle image : {form.imageFile.name}</span>
                                            </div>
                                        )}
                                        <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, imageFile: e.target.files?.[0] || null })} />
                                    </div>
                                </div>

                                <div className="cat-field">
                                    <label>Fichier (optionnel)</label>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {form.attachment && !form.file && (
                                            <div className="cat-doc">
                                                <a href={getFileUrl(form.attachment)} target="_blank" rel="noreferrer">
                                                    <FileText size={14} style={{ verticalAlign: "middle", marginRight: 5 }} />
                                                    Document actuel
                                                </a>
                                            </div>
                                        )}
                                        {form.file && (
                                            <div className="cat-doc">
                                                <span className="cat-file-pill">Nouveau fichier : {form.file.name}</span>
                                            </div>
                                        )}
                                        <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
                                    </div>
                                </div>
                            </div>

                            <div className="cat-divider" />

                            <div className="cat-formations">
                                <div className="cat-formations-head">
                                    <h4>Sélection des formations</h4>
                                    <div className="cat-search-wrap" style={{ width: 280 }}>
                                        <Search size={13} className="cat-search-icon" />
                                        <input
                                            className="cat-search"
                                            value={searchFormation}
                                            onChange={(e) => {
                                                setSearchFormation(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            placeholder="Rechercher formation..."
                                        />
                                    </div>
                                </div>

                                {/* ── Filters bar: Domaine + Thème ── */}
                                <div className="catalogue-filters-bar">
                                    <select
                                        className="catalogue-filter-select"
                                        value={selectedDomaine}
                                        onChange={(e) => {
                                            setSelectedDomaine(e.target.value);
                                            setSelectedTheme("");
                                            setCurrentPage(1);
                                            setSelectedFormationIds([]);
                                        }}
                                    >
                                        <option value="">Tous les domaines</option>
                                        {domaines.map(d => (
                                            <option
                                                key={d.id || d.reference || d.nom || d.libelle || d.name}
                                                value={d.id || d.reference || d.nom || d.libelle || d.name}
                                            >
                                                {d.nom || d.name || d.libelle}
                                            </option>
                                        ))}
                                    </select>

                                    <select
                                        className="catalogue-filter-select"
                                        value={selectedTheme}
                                        onChange={(e) => {
                                            setSelectedTheme(e.target.value);
                                            setCurrentPage(1);
                                            setSelectedFormationIds([]);
                                        }}
                                    >
                                        <option value="">Tous les thèmes</option>
                                        {themes.map(t => (
                                            <option
                                                key={t.id || t.reference || t.nom || t.libelle || t.name}
                                                value={t.id || t.reference || t.nom || t.libelle || t.name}
                                            >
                                                {t.nom || t.name || t.libelle}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* ── Selection toolbar ── */}
                                <div className="catalogue-selection-toolbar">
                                    <div className="catalogue-selection-summary">
                                        {selectedFormationIds.length} formation(s) sélectionnée(s)
                                        {(selectedDomaine || selectedTheme) && (
                                            <span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                                                · sélection sur formations filtrées
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="catalogue-select-all-btn"
                                        disabled={filteredFormationIds.length === 0}
                                        onClick={toggleSelectAllFiltered}
                                    >
                                        {areAllFilteredSelected
                                            ? "Désélectionner les formations filtrées"
                                            : "Tout sélectionner les formations filtrées"}
                                    </button>
                                </div>

                                <div className="cat-formations-table-wrap">
                                    <table className="cat-formations-table">
                                        <thead>
                                        <tr>
                                            <th style={{ width: 40, textAlign: "center" }}><Check size={13} /></th>
                                            <th style={{ width: 100 }}>Référence</th>
                                            <th>Formation</th>
                                            <th>Domaine / Thème</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {paginatedFormations.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="cat-empty-cell">
                                                    Aucune formation trouvée
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedFormations.map((f) => {
                                                const checked = selectedFormationIds.includes(f.id);
                                                return (
                                                    <tr
                                                        key={f.id}
                                                        className={checked ? "selected" : ""}
                                                        onClick={() => toggleFormationSelection(f.id)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                                            <div className={`cat-checkbox ${checked ? "checked" : ""}`}
                                                                 style={{ margin: "auto" }}
                                                                 onClick={() => toggleFormationSelection(f.id)}>
                                                                {checked && <Check size={10} />}
                                                            </div>
                                                        </td>
                                                        <td><span className="cat-badge cat-badge--blue">{f.reference}</span></td>
                                                        <td><div style={{ fontWeight: 600 }}>{f.description}</div></td>
                                                        <td>
                                                            <div className="cat-selected" style={{ marginTop: 0 }}>
                                                                {f.domaineNom || f.domaine?.nom || "-"}
                                                                <span style={{ margin: "0 4px", opacity: 0.5 }}>/</span>
                                                                {f.themeNom || f.theme?.nom || "-"}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ── Pagination controls ── */}
                                {filteredFormations.length > itemsPerPage && (
                                    <div className="cicada-pagination">
                                        <div className="cicada-pagination-info">
                                            Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredFormations.length)} sur {filteredFormations.length} formations
                                        </div>
                                        <div className="cicada-pagination-actions">
                                            <button
                                                type="button"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            >
                                                Précédent
                                            </button>
                                            <span>Page {currentPage} / {totalPages}</span>
                                            <button
                                                type="button"
                                                disabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            >
                                                Suivant
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="cat-selected" style={{ marginTop: 8 }}>
                                    Formations sélectionnées : <b>{selectedFormationIds.length}</b>
                                </div>
                            </div>
                        </div>

                        <div className="cat-modal-footer">
                            <button className="cat-btn cat-btn-light" onClick={closeModal} type="button">Annuler</button>
                            <button className="cat-btn cat-btn-blue" onClick={onSubmit} disabled={loading} type="button">
                                {loading ? "Enregistrement..." : isEdit ? "Enregistrer" : "Ajouter"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal Détails ── */}
            {detailsOpen && selectedCat && (
                <div className="cat-modal-overlay" onClick={handleCloseDetails}>
                    <div className="cat-modal cat-modal--large" onClick={(e) => e.stopPropagation()}>
                        <div className="cat-modal-header">
                            <h3>
                                <Eye size={16} style={{ color: "#dc3545" }} />
                                Détails du catalogue
                            </h3>
                            <button className="cat-x" onClick={handleCloseDetails} type="button">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="cat-modal-body">
                            <div className="cat-details-grid">
                                <div className="cat-details-section">
                                    <h4 className="cat-details-title">
                                        <Info size={14} /> Informations générales
                                    </h4>
                                    <div className="cat-info-card">
                                        <div className="cat-info-row">
                                            <span className="cat-info-label">Référence :</span>
                                            <span className="cat-info-value">{selectedCat.reference}</span>
                                        </div>
                                        <div className="cat-info-row">
                                            <span className="cat-info-label">Nom :</span>
                                            <span className="cat-info-value">{selectedCat.nom}</span>
                                        </div>
                                        <div className="cat-info-row">
                                            <span className="cat-info-label">Statut :</span>
                                            <span className={`cat-pill ${selectedCat.status === 1 ? "ok" : "off"}`}>
                                                {selectedCat.status === 1 ? "Actif" : "Inactif"}
                                            </span>
                                        </div>
                                        <div className="cat-info-row">
                                            <span className="cat-info-label">Date création :</span>
                                            <span className="cat-info-value">
                                                {selectedCat.createdAt ? new Date(selectedCat.createdAt).toLocaleDateString() : "-"}
                                            </span>
                                        </div>
                                        <div className="cat-info-row" style={{ flexDirection: "column", alignItems: "flex-start", border: "none" }}>
                                            <span className="cat-info-label">Description :</span>
                                            <p className="cat-info-desc">{selectedCat.description || "Aucune description"}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="cat-details-section">
                                    <h4 className="cat-details-title">
                                        <BookOpen size={14} /> Formations associées ({associatedFormations.length})
                                    </h4>
                                    <div className="cat-details-toolbar">
                                        <div className="cat-search-wrap cat-search-wrap--full">
                                            <Search size={14} className="cat-search-icon" />
                                            <input
                                                className="cat-search"
                                                value={detailFormationSearch}
                                                onChange={(e) => setDetailFormationSearch(e.target.value)}
                                                placeholder="Rechercher par titre, code, domaine ou theme..."
                                            />
                                        </div>
                                        {/*<div className="cat-details-meta">*/}
                                        {/*    {filteredAssociatedFormations.length} formation(s) trouvÃ©e(s)*/}
                                        {/*</div>*/}
                                    </div>
                                    <div className="cat-table-wrap cat-table-wrap--details">
                                        <table className="cat-table cat-table--small">
                                            <thead>
                                            <tr>
                                                <th>Référence</th>
                                                <th>Formation</th>
                                                <th>Domaine</th>
                                                <th>Thème</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {paginatedAssociatedFormations.length > 0 ? (
                                                paginatedAssociatedFormations.map((f) => (
                                                    <tr key={f.id}>
                                                        <td><span className="cat-badge cat-badge--blue">{f.reference || f.code || "-"}</span></td>
                                                        <td>
                                                            <div className="cat-formation-title">
                                                                {f.nom || f.name || f.title || f.description || "-"}
                                                            </div>
                                                        </td>
                                                        <td>
                                                                <span className="cat-selected cat-selected--inline">
                                                                    {f.domaineNom || f.domaine?.nom || "-"}
                                                                </span>
                                                        </td>
                                                        <td>
                                                                <span className="cat-selected cat-selected--inline">
                                                                    {f.themeNom || f.theme?.nom || "-"}
                                                                </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} className="cat-empty-cell">
                                                        Aucune formation associée à ce catalogue
                                                    </td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="cat-pagination">
                                        <button
                                            className="cat-btn cat-btn-light"
                                            onClick={() => setDetailFormationPage((p) => Math.max(1, p - 1))}
                                            disabled={detailCurrentPage === 1}
                                            type="button"
                                        >
                                            Précédent
                                        </button>
                                        <div className="cat-pagination-info">
                                            Page <b>{detailCurrentPage}</b> / <b>{detailTotalPages}</b>
                                        </div>
                                        <button
                                            className="cat-btn cat-btn-light"
                                            onClick={() => setDetailFormationPage((p) => Math.min(detailTotalPages, p + 1))}
                                            disabled={detailCurrentPage === detailTotalPages}
                                            type="button"
                                        >
                                            Suivant
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="cat-modal-footer">
                            <button className="cat-btn cat-btn-light" onClick={handleCloseDetails} type="button">Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
